import React, { useEffect, useRef } from 'react';
import i18n from 'i18next';
import FormsElementContainer from '../FormsElementContainer/FormsElementContainer';

// Components
import FormLabel from '../FormLabel/FormLabel';
import { createDescribedById as FormLabelCreateDescribedById } from '../FormLabel/utilities';
import { Icons, SVG } from '../../../../atoms/SVG';

import { FormLabelModifier } from '../FormLabel/enumerable';
import { TooltipPlacement } from '../../../../atoms/Tooltip';
import FormInput from '../FormInput/FormInput';
import { tryTranslate } from '../../../../../lib/translation/utils';
import { Namespace } from '../../../../../translation/namespace';
import Bem from '../../../../../lib/bemUtils/bem';

export type SelectOption = {
  text?: string;
  value: string;
  disabled?: boolean;
  readOnly?: boolean;
  ariaLabel?: string;
  hidden?: boolean;
  parameters?: any;
};

// optionsProcessor forces the user to specify input related translations
// in an `inputs` property of the translation file. Better take the same
// argument as `tryTranslate` (namespaceAndKey)
export function optionsProcessor(inputName: string, options: SelectOption[], translationNamespace?: string): any {
  return options.map((option) => {
    let postProcessedOption;
    if (option.text) {
      postProcessedOption = {
        value: option.value,
        text: option.text,
        disabled: option.disabled || option.readOnly,
      };
    } else {
      // needed to be able to add ariaLabel information for
      // options all text in upper case, see SON-4937. When
      // needed object is returned from translation providing
      // text and aria-label information, see phrases.js
      // securityClearance.
      const translation: string | any = tryTranslate(
        `${translationNamespace}:inputs.${inputName}.options.${option.value}`,
        { returnObjects: true, parameters: option.parameters },
        option.value,
      );

      postProcessedOption = {
        value: option.value,
        text: typeof translation === 'object' ? translation.text : translation,
        disabled: option.disabled || option.readOnly,
        ariaLabel: typeof translation === 'object' ? translation.ariaLabel : null,
      };
    }

    return postProcessedOption;
  });
}

type FormSelectType = {
  id: string;
  options?: SelectOption[];
  name: string;
  required?: boolean;
  disabled?: boolean;
  value?: number | string;
  defaultValue?: number | string;
  labelHidden?: boolean;
  labelTitle: string;
  labelHelpText?: string;
  tooltipPlacement?: TooltipPlacement;
  hidePlaceHolder?: boolean;
  validationMessage?: string;
  modifierLabel?: FormLabelModifier;
  ariaControls?: string;
  modifier?: string;
  // Events
  onChange?: any;
};

/**
 * Renders a select tag for a form.
 */

export default function FormSelect({
  id,
  options = [],
  name,
  required = false,
  disabled = false,
  value,
  defaultValue,
  labelHidden = false,
  labelTitle,
  labelHelpText,
  tooltipPlacement,
  hidePlaceHolder = false,
  validationMessage = '',
  modifierLabel,
  ariaControls,
  modifier,
  onChange = () => {},
}: FormSelectType) {
  const selectRef = useRef<HTMLSelectElement | null>(null);

  const setCustomValidity = (message = '') => {
    if (selectRef.current) {
      if (selectRef.current.setCustomValidity && message) {
        selectRef.current.setCustomValidity(message);
        return true;
      }

      selectRef.current.setCustomValidity('');
      return false;
    }

    return true;
  };

  useEffect(() => {
    setCustomValidity(validationMessage);
  }, [validationMessage]);

  const idSelect = () => {
    // id was not updated on component update when created in constructor
    return `${name}-${id}`;
  };

  /**
   *  Create default option if select is required, because a required select has to have a See SON-3697!
   * @returns {{hidden: boolean, text, value: string}[]|*[]}
   */
  const getPlaceHolder = (): SelectOption[] => {
    const placeHolder = (text: string) => ({ value: '', text, hidden: false });
    // required needs placeholder, will override showPlaceHolder
    if (required) {
      return [placeHolder(i18n.t(`${Namespace.phrases}:selectPlaceholderOptionLabel`))];
    }
    // if not required we can hide placeholder or show the default one
    return hidePlaceHolder ? [] : [placeHolder(i18n.t(`${Namespace.phrases}:selectPlaceholderOptionNotRequiredLabel`))];
  };

  const mapOptionsToHtml = (rawOptions: SelectOption[]) => {
    const defaultPlaceHolder = getPlaceHolder();

    // concat so we don't work on props and inject it on first place of options array.
    return defaultPlaceHolder.concat(rawOptions).map((option: SelectOption) => {
      return (
        <option
          className="form-select__option"
          value={option.value}
          hidden={option.hidden}
          disabled={option.disabled}
          key={`${name}-${option.value}-${option.text}`}
          aria-label={option.ariaLabel}
        >
          {option.text}
        </option>
      );
    });
  };

  /**
   * If we have only one option, we don't return a select
   * but a Readonly input with a corresponding hidden input
   * with the real "preselected" value
   * @return {*}
   */
  const renderReadonlyInput = (rawOptions: SelectOption[]) => {
    return (
      <>
        <FormInput
          id={idSelect()}
          name="valuePreview"
          labelTitle={labelTitle}
          required={required}
          labelHidden={labelHidden}
          labelHelpText={labelHelpText}
          tooltipPlacement={tooltipPlacement}
          value={rawOptions[0].text}
          readOnly
        />
        <input type="hidden" name={name} value={rawOptions[0].value} />
      </>
    );
  };

  const renderSelect = (rawOptions: SelectOption[]) => {
    return (
      <FormsElementContainer>
        {!labelHidden && (
          <FormLabel
            htmlFor={idSelect()}
            required={required}
            hidden={labelHidden}
            helpText={labelHelpText}
            tooltipPlacement={tooltipPlacement}
            modifier={modifierLabel}
          >
            {labelTitle}
          </FormLabel>
        )}

        <span className="form-select__container">
          <select
            id={idSelect()}
            ref={selectRef}
            className={Bem('form-select', modifier)}
            name={name}
            required={required}
            disabled={disabled}
            value={value} // => value of selected option for controlled components
            defaultValue={defaultValue} // => value of selected option for uncontrolled components
            // Accessibility
            aria-describedby={labelHelpText && FormLabelCreateDescribedById(idSelect())}
            aria-label={labelHidden ? labelTitle : undefined}
            // Events
            onChange={onChange}
            aria-controls={ariaControls}
          >
            {mapOptionsToHtml(rawOptions)}
          </select>
          <span className="form-select__button">
            <SVG svgSpriteId={Icons.ARROW_DOWN} />
          </span>
        </span>
      </FormsElementContainer>
    );
  };

  if (required && options.length === 1) {
    return renderReadonlyInput(options);
  }
  return renderSelect(options);
}
